import ErrorMessage from 'components/elements/items/ErrorMessage'
import FormTitle from 'components/elements/items/FormTitle'
import TextBox from 'components/elements/items/TextBox'
import Text from 'components/elements/texts/Text'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import {
  convertWideKanaToHalfKana,
  validateFullName,
  validatePhoneNumber,
} from 'utils/commonUtils'

/**
 * 架電モーダル 呼び出し元を含む
 */
export const ContractInfoInModal = (
  props: ContractInfoInModalProps
): JSX.Element => {
  const {
    placeholderLastName,
    placeholderFirstName,
    placeholderFirstNumber,
    placeholderSecondNumber,
    placeholderLastNumber,
    inputTextLastName,
    inputTextFirstName,
    inputTextFirstNumber,
    inputTextSecondNumber,
    inputTextLastNumber,
    setLastName,
    setFirstName,
    onChangeFirstNumber,
    onChangeSecondNumber,
    onChangeLastNumber,
    onErrorChangePhoneNumber,
    onErrorChangeFullName,
  } = props

  const [hasFullNameError, setHasFullNameError] = useState(true)
  const [hasPhoneNumberError, setHasPhoneError] = useState(true)

  // --- バリデーション ---
  const phoneNumberError = useMemo(() => {
    const errorMessageHalfwidthFullwidthNumberType =
      '全角・半角数字で入力してください。入力内容はオペレーターに送信されません。'
    const errorMessagePhoneNumberLength =
      '電話番号の桁数が不足しています。入力内容はオペレーターに送信されません。'
    const error = validatePhoneNumber(
      inputTextFirstNumber,
      inputTextSecondNumber,
      inputTextLastNumber,
      errorMessageHalfwidthFullwidthNumberType,
      errorMessagePhoneNumberLength
    )

    if (inputTextFirstNumber || inputTextSecondNumber || inputTextLastNumber) {
      if (error) {
        setHasPhoneError(true)
        return error
      }
      setHasPhoneError(false)
      return
    }
  }, [inputTextFirstNumber, inputTextSecondNumber, inputTextLastNumber])

  const fullNameError = useMemo(() => {
    const errorMessageKanaType =
      '全角・半角カナ文字で入力してください。入力内容はオペレーターに送信されません。'
    const error = validateFullName(
      inputTextFirstName,
      inputTextLastName,
      errorMessageKanaType
    )
    if (inputTextFirstName || inputTextLastName) {
      if (error) {
        setHasFullNameError(true)
        return error
      }
      setHasFullNameError(false)
    }
  }, [inputTextFirstName, inputTextLastName])

  useEffect(() => {
    onErrorChangePhoneNumber(hasPhoneNumberError)
    onErrorChangeFullName(hasFullNameError)
  }, [
    hasPhoneNumberError,
    hasFullNameError,
    onErrorChangePhoneNumber,
    onErrorChangeFullName,
  ])

  /** 要請者氏名の残り入力文字数の算出（スペースを除く） */
  const getRemainingLengthForNameField = (partnerText: string): number =>
    29 - convertWideKanaToHalfKana(partnerText).length

  const handleNameTextChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
    setState: React.Dispatch<React.SetStateAction<string>>,
    partnerText: string
  ): void => {
    const targetValue = event.target.value

    if (
      convertWideKanaToHalfKana(targetValue).length >
      getRemainingLengthForNameField(partnerText)
    ) {
      /** 入力テキストが文字数制限を超えている場合は変更通知を送らない */
      return
    }
    setState(targetValue)
  }

  return (
    <InputFormArea>
      <InputFormWrap>
        <StyledFormTitle title="ご要請者電話番号" isOption bold />
        <InputInnerWrap>
          <StyledInput
            placeholder={placeholderFirstNumber}
            inputText={inputTextFirstNumber}
            maxLength={3}
            onChange={(e): void => onChangeFirstNumber(e.target.value)}
            doNotPlaceholderMove={true}
          />
          <StyledInput
            placeholder={placeholderSecondNumber}
            inputText={inputTextSecondNumber}
            maxLength={4}
            onChange={(e): void => onChangeSecondNumber(e.target.value)}
            doNotPlaceholderMove={true}
          />
          <StyledInput
            placeholder={placeholderLastNumber}
            inputText={inputTextLastNumber}
            maxLength={4}
            onChange={(e): void => onChangeLastNumber(e.target.value)}
            doNotPlaceholderMove={true}
          />
        </InputInnerWrap>
      </InputFormWrap>
      {phoneNumberError && (
        <ErrorMessage
          message={phoneNumberError}
          justifyContent="left"
          marginNone={true}
        />
      )}
      <InputFormWrap>
        <StyledFormTitle title="ご要請者お名前（カナ）" isOption bold />
        <InputInnerWrap>
          <NameInputWrap>
            <StyledFormTitle bold title="氏:" />
            <StyledNameInput
              maxLength={getRemainingLengthForNameField(inputTextFirstName)}
              placeholder={placeholderLastName}
              inputText={inputTextLastName}
              onChange={(e): void =>
                handleNameTextChange(e, setLastName, inputTextFirstName)
              }
              doNotPlaceholderMove={true}
            />
          </NameInputWrap>
          <NameInputWrap>
            <StyledFormTitle bold title="名:" />
            <StyledNameInput
              maxLength={getRemainingLengthForNameField(inputTextLastName)}
              placeholder={placeholderFirstName}
              inputText={inputTextFirstName}
              onChange={(e): void =>
                handleNameTextChange(e, setFirstName, inputTextLastName)
              }
              doNotPlaceholderMove={true}
            />
          </NameInputWrap>
        </InputInnerWrap>
      </InputFormWrap>
      {fullNameError && (
        <ErrorMessage
          message={fullNameError}
          justifyContent="left"
          marginNone={true}
        />
      )}
      <HelpInfoWrap>
        <HelpInfo>
          {'※'}
          <Span>{'電話番号とお名前を入力いただくことで'}</Span>
          {'、内容がオペレーターに送信されます。'}
        </HelpInfo>
        <HelpInfo>
          {
            '※お電話がつながらない場合、後ほどオペレーターからご連絡いたします。ただし、お時間をいただくことがあります。なお、電話番号が入力されていない場合は折り返しのご連絡はできません。'
          }
        </HelpInfo>
        <HelpInfo>
          {'※'}
          <Span>{'電話番号とお名前のどちらも入力されていない場合'}</Span>
          {'、内容は送信されません。'}
        </HelpInfo>
      </HelpInfoWrap>
    </InputFormArea>
  )
}

type ContractInfoInModalProps = {
  placeholderFirstName: string
  placeholderLastName: string
  placeholderFirstNumber: string
  placeholderSecondNumber: string
  placeholderLastNumber: string
  inputTextFirstName: string
  inputTextLastName: string
  inputTextFirstNumber: string
  inputTextSecondNumber: string
  inputTextLastNumber: string
  setFirstName: React.Dispatch<React.SetStateAction<string>>
  setLastName: React.Dispatch<React.SetStateAction<string>>
  onChangeFirstNumber: React.Dispatch<React.SetStateAction<string>>
  onChangeSecondNumber: React.Dispatch<React.SetStateAction<string>>
  onChangeLastNumber: React.Dispatch<React.SetStateAction<string>>
  onErrorChangePhoneNumber: (hasError: boolean) => void
  onErrorChangeFullName: (hasFullNameError: boolean) => void
}

const StyledFormTitle = styled(FormTitle)`
  margin: 0;
  padding: 3px;
  border-radius: 1px;
  background-color: var(--color-background);
  display: flex;
  align-items: center;
`

const InputFormArea = styled.form`
  display: flex;
  flex-direction: column;
  gap: calc(10vw / 3.75);
`

const InputFormWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: calc(10vw / 3.75);
`

const InputInnerWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const StyledInput = styled(TextBox)`
  width: 100%;
  margin: 0;
  padding: 10px;
  border-radius: 1px;
  border: none;
  background-color: var(--color-background);
  transition: all 0.2s ease;
  font-size: 16px;
  text-align: center;
  &:focus {
    outline: none;
    border-bottom: none;
  }
  &::placeholder {
    color: #aaa;
  }
`
const NameInputWrap = styled.div`
  width: 100%;
  display: flex;
`
const StyledNameInput = styled(StyledInput)`
  text-align: left;
`
const HelpInfoWrap = styled.div`
  padding: 5px;
  background-color: var(--color-background);
`
const Span = styled(Text)`
  display: initial;
  font-size: 13px;
  border-bottom: 1px solid var(--color-error);
  color: var(--color-error);
`
const HelpInfo = styled(Text)`
  font-size: 13px;
  color: var(--color-text);
  margin: 0;
  &&:nth-of-type(2),
  :nth-of-type(3) {
    margin-top: 8px;
  }
`
